import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Filter, Printer, Users } from 'lucide-react';

const AfterschoolReportModal = ({ apiCall, onClose, showError, showSuccess, selectedYear }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    academicYear: selectedYear || new Date().getFullYear().toString(),
    packageId: '',
    timeSlot: '',
    status: 'Active'
  });
  const [showFilters, setShowFilters] = useState(true);
  const printRef = useRef(null);
  const isPrintingRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = `/afterschool/enrollments?academicYear=${filters.academicYear}`;
      
      const enrollmentsData = await apiCall(query);
      
      // Apply client-side filters
      let filtered = enrollmentsData || [];
      
      if (filters.packageId) {
        filtered = filtered.filter(e => e.PackageID === parseInt(filters.packageId));
      }
      
      if (filters.timeSlot) {
        filtered = filtered.filter(e => e.TimeSlot === filters.timeSlot);
      }
      
      if (filters.status) {
        filtered = filtered.filter(e => e.Status === filters.status);
      }
      
      setEnrollments(filtered);
      
      // Load packages if not loaded
      if (packages.length === 0) {
        const packagesData = await apiCall('/afterschool/packages');
        setPackages(packagesData || []);
      }
    } catch (error) {
      console.error('Error loading afterschool report:', error);
      if (showError) showError('Error loading report: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filters, packages.length, showError]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    loadData();
  };

  const clearFilters = () => {
    setFilters({
      academicYear: selectedYear || new Date().getFullYear().toString(),
      packageId: '',
      timeSlot: '',
      status: 'Active'
    });
    setTimeout(() => loadData(), 100);
  };

  const exportCSV = () => {
    if (enrollments.length === 0) {
      if (showError) showError('No data to export');
      return;
    }

    const headers = ['Family ID', 'Student Name', 'Grade', 'Package', 'Time Slot', 'Sessions', 'Final Cost', 'Amount Paid', 'Balance', 'Status', 'Academic Year'];
    
    const data = enrollments.map(e => [
      e.FamilyID,
      `${e.FirstName} ${e.LastName}`,
      e.Grade || 'N/A',
      e.PackageName,
      e.TimeSlot || 'Not Specified',
      e.Sessions,
      (e.FinalCost || 0).toFixed(2),
      (e.AmountPaid || 0).toFixed(2),
      (e.BalanceRemaining || 0).toFixed(2),
      e.Status,
      e.AcademicYear
    ]);

    const csv = [headers, ...data].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `afterschool_report_${filters.academicYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    if (showSuccess) showSuccess('Report exported successfully');
  };

  const handlePrint = () => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;

    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      if (showError) showError('Please allow popups to print the report');
      isPrintingRef.current = false;
      return;
    }

    const printContent = printRef.current;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Afterschool Enrollment Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: white; }
            
            .report-header {
              border-bottom: 3px solid #1f2937;
              padding-bottom: 15px;
              margin-bottom: 25px;
            }
            
            .report-header h1 {
              font-size: 28px;
              color: #1f2937;
              margin-bottom: 8px;
            }
            
            .report-header .info {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              color: #4b5563;
            }
            
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 25px;
            }
            
            .stat-card {
              border: 2px solid #d1d5db;
              border-radius: 8px;
              padding: 15px;
            }
            
            .stat-card .label {
              font-size: 11px;
              color: #6b7280;
              font-weight: 600;
              margin-bottom: 5px;
            }
            
            .stat-card .value {
              font-size: 24px;
              font-weight: bold;
            }
            
            .section {
              border: 2px solid #d1d5db;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            
            .section h3 {
              font-size: 16px;
              color: #1f2937;
              margin-bottom: 12px;
            }
            
            .breakdown-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            
            .breakdown-item {
              border: 2px solid #e5e7eb;
              border-radius: 6px;
              padding: 12px;
              background: #f9fafb;
            }
            
            .breakdown-item h4 {
              font-size: 13px;
              color: #1f2937;
              margin-bottom: 8px;
            }
            
            .breakdown-item .detail {
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              margin-bottom: 3px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            
            thead tr {
              background: #f3f4f6;
              border: 2px solid #d1d5db;
            }
            
            th {
              padding: 8px 6px;
              text-align: left;
              font-weight: bold;
              color: #374151;
              border: 1px solid #d1d5db;
            }
            
            tbody tr {
              border: 1px solid #d1d5db;
            }
            
            td {
              padding: 6px;
              border: 1px solid #d1d5db;
            }
            
            tfoot tr {
              background: #e5e7eb;
              border: 2px solid #d1d5db;
              font-weight: bold;
            }
            
            .report-footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 2px solid #d1d5db;
              text-align: center;
              font-size: 10px;
              color: #6b7280;
            }
            
            @media print {
              @page { margin: 1.5cm; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          isPrintingRef.current = false;
        }, 100);
      }, 250);
    };
  };

  // Analytics
  const totalStudents = enrollments.length;
  const activeStudents = enrollments.filter(e => e.Status === 'Active').length;
  const totalRevenue = enrollments.reduce((sum, e) => sum + (e.AmountPaid || 0), 0);
  const totalOutstanding = enrollments.reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0);

  // Group by package
  const packageStats = {};
  enrollments.forEach(e => {
    if (!packageStats[e.PackageName]) {
      packageStats[e.PackageName] = {
        students: 0,
        revenue: 0,
        outstanding: 0
      };
    }
    packageStats[e.PackageName].students += 1;
    packageStats[e.PackageName].revenue += e.AmountPaid || 0;
    packageStats[e.PackageName].outstanding += e.BalanceRemaining || 0;
  });

  // Group by time slot
  const timeSlotStats = {};
  enrollments.forEach(e => {
    const slot = e.TimeSlot || 'Not Specified';
    if (!timeSlotStats[slot]) {
      timeSlotStats[slot] = {
        students: 0,
        packages: new Set()
      };
    }
    timeSlotStats[slot].students += 1;
    timeSlotStats[slot].packages.add(e.PackageName);
  });

  // Get unique time slots for filter
  const uniqueTimeSlots = [...new Set(enrollments.map(e => e.TimeSlot).filter(Boolean))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                <Users size={28} />
                Afterschool Enrollment Report
              </h2>
              <p className="text-green-100">Detailed enrollment analysis by package and time slot</p>
            </div>
            <div className="flex gap-3 items-center">
              <button
                onClick={handlePrint}
                className="bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all"
              >
                <Printer size={18} />
                Print
              </button>
              <button
                onClick={exportCSV}
                disabled={enrollments.length === 0}
                className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 transition-all"
              >
                <Download size={18} />
                Export CSV
              </button>
              <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors">
                <X size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 pb-0">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Filter size={18} />
                Filters
              </h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
              >
                {showFilters ? 'Hide' : 'Show'}
              </button>
            </div>

            {showFilters && (
              <div className="space-y-3">
                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Academic Year</label>
                    <input
                      type="text"
                      value={filters.academicYear}
                      onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Package</label>
                    <select
                      value={filters.packageId}
                      onChange={(e) => setFilters({...filters, packageId: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">All Packages</option>
                      {packages.map(p => (
                        <option key={p.PackageID} value={p.PackageID}>
                          {p.PackageName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Time Slot</label>
                    <select
                      value={filters.timeSlot}
                      onChange={(e) => setFilters({...filters, timeSlot: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">All Time Slots</option>
                      {uniqueTimeSlots.map(slot => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={applyFilters}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                <p className="text-gray-600">Loading report...</p>
              </div>
            </div>
          ) : (
            <div ref={printRef} className="space-y-6">
              {/* Print Header */}
              <div className="report-header">
                <h1>Afterschool Enrollment Report</h1>
                <div className="info">
                  <div>
                    <p><strong>Academic Year:</strong> {filters.academicYear}</p>
                    <p><strong>Generated:</strong> {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
                  </div>
                  <div style={{textAlign: 'right'}}>
                    <p><strong>School Management System</strong></p>
                    <p>Afterschool Program Report</p>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="label">Total Students</div>
                  <div className="value">{totalStudents}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Active Students</div>
                  <div className="value" style={{color: '#16a34a'}}>{activeStudents}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Total Revenue</div>
                  <div className="value" style={{color: '#2563eb'}}>${totalRevenue.toFixed(2)}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Outstanding</div>
                  <div className="value" style={{color: '#dc2626'}}>${totalOutstanding.toFixed(2)}</div>
                </div>
              </div>

              {/* Package Breakdown */}
              {Object.keys(packageStats).length > 0 && (
                <div className="section">
                  <h3>Enrollment by Package</h3>
                  <div className="breakdown-grid">
                    {Object.entries(packageStats).map(([packageName, stats]) => (
                      <div key={packageName} className="breakdown-item">
                        <h4>{packageName}</h4>
                        <div className="detail">
                          <span className="label">Students:</span>
                          <span className="value">{stats.students}</span>
                        </div>
                        <div className="detail">
                          <span className="label">Revenue:</span>
                          <span className="value" style={{color: '#2563eb'}}>${stats.revenue.toFixed(2)}</span>
                        </div>
                        <div className="detail">
                          <span className="label">Outstanding:</span>
                          <span className="value" style={{color: '#dc2626'}}>${stats.outstanding.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Time Slot Breakdown */}
              {Object.keys(timeSlotStats).length > 0 && (
                <div className="section">
                  <h3>Enrollment by Time Slot</h3>
                  <div className="breakdown-grid">
                    {Object.entries(timeSlotStats).map(([timeSlot, stats]) => (
                      <div key={timeSlot} className="breakdown-item">
                        <h4>{timeSlot}</h4>
                        <div className="detail">
                          <span className="label">Students:</span>
                          <span className="value">{stats.students}</span>
                        </div>
                        <div className="detail">
                          <span className="label">Packages:</span>
                          <span className="value">{stats.packages.size}</span>
                        </div>
                        <div style={{fontSize: '10px', marginTop: '6px', color: '#6b7280'}}>
                          {Array.from(stats.packages).join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Student List by Package */}
              {Object.entries(packageStats).map(([packageName, stats]) => {
                const studentsInPackage = enrollments.filter(e => e.PackageName === packageName);
                
                return (
                  <div key={packageName} className="section">
                    <h3>{packageName} - Students List ({studentsInPackage.length})</h3>
                    <table>
                      <thead>
                        <tr>
                          <th>Family</th>
                          <th>Student Name</th>
                          <th>Grade</th>
                          <th>Time Slot</th>
                          <th>Sessions</th>
                          <th>Cost</th>
                          <th>Paid</th>
                          <th>Balance</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentsInPackage.length === 0 ? (
                          <tr>
                            <td colSpan="9" style={{textAlign: 'center', padding: '20px'}}>
                              No students enrolled
                            </td>
                          </tr>
                        ) : (
                          studentsInPackage.map(student => (
                            <tr key={student.EnrollmentID}>
                              <td style={{fontWeight: '600'}}>{student.FamilyID}</td>
                              <td>{student.FirstName} {student.LastName}</td>
                              <td>{student.Grade || 'N/A'}</td>
                              <td>{student.TimeSlot || 'Not Specified'}</td>
                              <td>{student.Sessions}</td>
                              <td>${(student.FinalCost || 0).toFixed(2)}</td>
                              <td style={{color: '#16a34a', fontWeight: '600'}}>
                                ${(student.AmountPaid || 0).toFixed(2)}
                              </td>
                              <td style={{color: '#dc2626', fontWeight: '600'}}>
                                ${(student.BalanceRemaining || 0).toFixed(2)}
                              </td>
                              <td>
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  background: student.Status === 'Active' ? '#dcfce7' : '#f3f4f6',
                                  color: student.Status === 'Active' ? '#166534' : '#4b5563'
                                }}>
                                  {student.Status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {/* Footer */}
              <div className="report-footer">
                <p>This report was generated automatically by the School Management System</p>
                <p style={{marginTop: '5px'}}>Page printed on: {new Date().toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inline CSS */}
      <style jsx>{`
        .report-header {
          border-bottom: 3px solid #1f2937;
          padding-bottom: 15px;
          margin-bottom: 25px;
        }
        
        .report-header h1 {
          font-size: 24px;
          color: #1f2937;
          margin-bottom: 8px;
          font-weight: bold;
        }
        
        .report-header .info {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #4b5563;
        }
        
        .report-header .info strong {
          color: #1f2937;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .stat-card {
          border: 2px solid #d1d5db;
          border-radius: 8px;
          padding: 15px;
          background: white;
        }
        
        .stat-card .label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 600;
          margin-bottom: 5px;
        }
        
        .stat-card .value {
          font-size: 24px;
          font-weight: bold;
        }
        
        .section {
          border: 2px solid #d1d5db;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
          background: white;
        }
        
        .section h3 {
          font-size: 16px;
          color: #1f2937;
          margin-bottom: 12px;
          font-weight: bold;
        }
        
        .breakdown-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        
        .breakdown-item {
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px;
          background: #f9fafb;
        }
        
        .breakdown-item h4 {
          font-size: 13px;
          color: #1f2937;
          margin-bottom: 8px;
          font-weight: bold;
        }
        
        .breakdown-item .detail {
          display: flex;
          justify-between;
          font-size: 11px;
          margin-bottom: 3px;
        }
        
        .breakdown-item .detail .label {
          color: #6b7280;
        }
        
        .breakdown-item .detail .value {
          font-weight: 600;
        }
        
        .section table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        
        .section thead tr {
          background: #f3f4f6;
          border: 2px solid #d1d5db;
        }
        
        .section th {
          padding: 10px 8px;
          text-align: left;
          font-weight: bold;
          color: #374151;
          border: 1px solid #d1d5db;
        }
        
        .section tbody tr {
          border: 1px solid #d1d5db;
        }
        
        .section td {
          padding: 8px;
          border: 1px solid #d1d5db;
        }
        
        .report-footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 2px solid #d1d5db;
          text-align: center;
          font-size: 10px;
          color: #6b7280;
        }

        @media screen and (max-width: 768px) {
          .stats-grid,
          .breakdown-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AfterschoolReportModal;